import { Box, Container, styled, type BoxProps, type Theme } from '@mui/material';
import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { useUniqueId } from '~/hooks/unique-id';

export const MetaItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 2),
  borderRight: `1px solid ${theme.palette.borders.light}`,
  '&.last': {
    borderWidth: 0,
  },
}));

const settingsHeadStyles = (theme: Theme) => ({
  margin: 0,
  borderRadius: '5px 5px 0 0',
  padding: '12px',
  fontWeight: '600',
  fontSize: '13px',
  backgroundColor: theme.palette.background.default,
  textTransform: 'uppercase' as const,
  border: `1px solid ${theme.palette.borders.dark}`,
  borderBottom: 'none',
  '&.with-border-bottom': {
    borderBottom: `1px solid ${theme.palette.borders.dark}`,
  },
});

// A section heading under the page's h1.
export const SettingsHead = styled('h2')(({ theme }) => settingsHeadStyles(theme));

/**
 * A `SettingsHead` bar with content beside its heading, such as a link or a logo. Put that in
 * the bar next to a `SettingsHeadTitle` rather than inside the heading, which may hold only
 * phrasing content and is named by everything in it.
 */
export const SettingsHeadBar = styled('div')(({ theme }) => ({
  ...settingsHeadStyles(theme),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

/** The heading inside a `SettingsHeadBar`, which carries the bar's styling. */
export const SettingsHeadTitle = styled('h2')({ margin: 0, font: 'inherit' });

export const SettingsBody = styled('div')({
  backgroundColor: 'white',
});

export const SettingsIntro = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${theme.palette.borders.dark}`,
  borderBottom: 'none',
  '& .MuiAlert-message': {
    '& code': {
      fontSize: '13px',
    },
    fontSize: '14px',
  },
}));

/**
 * Ids shared by the label, description and control of one settings row, so the control is
 * named by the row's `SettingName` and described by its `SettingDescription`.
 */
export interface SettingIds {
  /** The id the row's control should take, which `SettingName` points at with `htmlFor`. */
  inputId: string;
  labelId: string;
  /** Set only while the row renders a `SettingDescription`, so nothing points at a missing id. */
  descriptionId: string | undefined;
}

interface SettingRow {
  ids: SettingIds;
  descriptionId: string;
  setDescribed: Dispatch<SetStateAction<boolean>>;
}

const SettingRowContext = createContext<SettingRow | null>(null);

/** The ids of the enclosing settings row, or null outside one. */
export const useSettingIds = (): SettingIds | null => useContext(SettingRowContext)?.ids ?? null;

const SettingsDataRoot = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.borders.dark}`,
  borderBottom: 'none',
  padding: '20px 24px 16px 16px',
  display: 'flex',
  justifyContent: 'space-between',
  // Label and control stack on phones so neither side is squeezed to a sliver. Tile rows
  // (`.scroll`) wrap instead, below.
  '&:not(.scroll)': {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
  },
  '&.last': {
    borderBottom: `1px solid ${theme.palette.borders.dark}`,
    borderRadius: '0px 0px 5px 5px',
  },
  '&.scroll': {
    overflowX: 'auto',
    // Rows of tiles (device commands, quick info) wrap on phones instead of scrolling sideways.
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      rowGap: theme.spacing(2),
    },
  },
  '&.no-space-between': {
    justifyContent: 'inherit',
  },
}));

/** One settings row. Its `SettingName` labels the control inside its `SettingInputWrapper`. */
export const SettingsData = ({ children, ...props }: BoxProps) => {
  const id = useUniqueId('setting');
  const [described, setDescribed] = useState(false);
  const row = useMemo<SettingRow>(
    () => ({
      ids: {
        inputId: `${id}-input`,
        labelId: `${id}-label`,
        descriptionId: described ? `${id}-description` : undefined,
      },
      descriptionId: `${id}-description`,
      setDescribed,
    }),
    [described, id],
  );
  return (
    <SettingsDataRoot {...props}>
      <SettingRowContext.Provider value={row}>{children}</SettingRowContext.Provider>
    </SettingsDataRoot>
  );
};

const settingNameStyles = { fontSize: '16px' } as const;
const SettingNameLabel = styled('label')(settingNameStyles);
const SettingNameText = styled('span')(settingNameStyles);

/**
 * The row's name. Inside a `SettingsData` it is a `<label>` for the row's control; elsewhere
 * (read-only detail rows) it is plain text.
 */
export const SettingName = ({
  children,
  ...props
}: {
  children?: ReactNode;
  className?: string;
}) => {
  const ids = useSettingIds();
  if (!ids) return <SettingNameText {...props}>{children}</SettingNameText>;
  return (
    <SettingNameLabel htmlFor={ids.inputId} id={ids.labelId} {...props}>
      {children}
    </SettingNameLabel>
  );
};

export const SettingFieldRequired = styled('span')(({ theme }) => ({
  color: theme.palette.error.main,
  marginLeft: '3px',
}));

const SettingDescriptionRoot = styled('p')(({ theme }) => ({
  fontSize: '13px',
  paddingTop: '4px',
  color: theme.palette.text.secondary,
}));

/** The row's help text, which the row's control references with `aria-describedby`. */
export const SettingDescription = ({
  children,
  ...props
}: {
  children?: ReactNode;
  className?: string;
}) => {
  const row = useContext(SettingRowContext);
  // Before paint, so the row's control never commits an aria-describedby to a missing id.
  useLayoutEffect(() => {
    if (!row) return;
    row.setDescribed(true);
    return () => row.setDescribed(false);
  }, [row]);
  return (
    <SettingDescriptionRoot id={row?.descriptionId} {...props}>
      {children}
    </SettingDescriptionRoot>
  );
};

export const SettingInputWrapper = styled('div')(({ theme }) => ({
  width: '35%',
  marginBottom: theme.spacing(0.5),
  '& .MuiFormHelperText-root': {
    marginTop: '-1px',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

export const SettingLabelWrapper = styled('div')(({ theme }) => ({
  width: '65%',
  '&.nested': {
    width: `calc(65% - ${theme.spacing(4)})`,
    marginLeft: theme.spacing(4),
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

export interface FormTableData {
  heading?: ReactNode;
  subHeading?: ReactNode;
  dataField?: ReactNode;
  nested?: boolean;
}
interface FormTableProps {
  formHeading?: ReactNode;
  formData: Array<FormTableData>;
}

export const FormLayout = ({ formHeading, formData }: FormTableProps) => (
  <Container sx={{ padding: { xs: 0 } }} maxWidth={false}>
    <div>
      <SettingsHead>{formHeading}</SettingsHead>
      <SettingsBody>
        {formData.map((field, idx) => (
          <SettingsData
            className={idx === formData.length - 1 ? 'last' : ''}
            key={`form-layout-${idx}`}
          >
            <SettingLabelWrapper className={field.nested ? 'nested' : undefined}>
              {field.heading && <SettingName>{field.heading}</SettingName>}
              {field.subHeading && <SettingDescription>{field.subHeading}</SettingDescription>}
            </SettingLabelWrapper>
            {field.dataField && <SettingInputWrapper>{field.dataField}</SettingInputWrapper>}
          </SettingsData>
        ))}
      </SettingsBody>
    </div>
  </Container>
);
